<template>
    <div v-if="event" class="space-y-4">
        <FlexibleCard headline-id="organisation" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('organisation')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('organisation')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('organisation')"
                    @enter-edit-mode="onEnterEditMode('organisation', $t('organisational-categorization'))"
                    @leave-edit-mode="leaveEditMode('organisation')"
                    @discard-changes="discardChanges('organisation')"
                    @save="saveForm('organisation')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('organisational-categorization') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventOrganisationForm
                        v-if="showForm('organisation')"
                        ref="eventOrganisationFormRef"
                        :event-id="eventId"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('company-group')"
                            :text="valueToString(event.companyGroup)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('business-domain')"
                            :text="valueToString(event.businessDomain)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('department')"
                            :text="valueToString(event.department)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="teamStructure" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('teamStructure')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('teamStructure')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('teamStructure')"
                    @enter-edit-mode="onEnterEditMode('teamStructure', $t('team-structure'))"
                    @leave-edit-mode="leaveEditMode('teamStructure')"
                    @discard-changes="discardChanges('teamStructure')"
                    @save="saveForm('teamStructure')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('team-structure') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventTeamStructureForm
                        v-if="showForm('teamStructure')"
                        ref="eventTeamStructureFormRef"
                        :event-id="eventId"
                        class="p-5"
                    />
                    <div v-else>
                        <CardLabelContent :label="$t('responsible')" :has-border-bottom="true">
                            <UserReferenceStatic mode="Multiple" :assigned-users="responsibleUsers" />
                        </CardLabelContent>
                        <CardRowText
                            :label="$t('responsible-user-notes')"
                            :text="valueToString(event.responsibleUserNotes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('assistant')"
                            :text="valueToString(event.assistant)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('team-changes')"
                            :text="valueToString(event.teamChanges)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardLabelContent from '@/components/Card/CardLabelContent.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import UserReferenceStatic from '@/components/UserReference/UserReferenceStatic.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useEventResponsibleUsers } from '@/event/composables/useEventResponsibleUsers';
import EventOrganisationForm from '@/event/forms/event/EventOrganisationForm.vue';
import EventTeamStructureForm from '@/event/forms/event/EventTeamStructureForm.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { useFluent } from 'fluent-vue';
import { computed, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const props = defineProps<{ eventId: string }>();
const fluent = useFluent();

const eventId = computed(() => props.eventId);
const { event } = useEventInjection();
const { responsibleUsers } = useEventResponsibleUsers();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const eventOrganisationFormRef = useTemplateRef('eventOrganisationFormRef');
const eventTeamStructureFormRef = useTemplateRef('eventTeamStructureFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('organisation', eventOrganisationFormRef);
register('teamStructure', eventTeamStructureFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
</script>
